import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNavigate } from 'react-router-dom';

import store from 'store';

import * as api from 'api/methods';

import { STATUSES, USER_ROLES } from 'constants/common';

import { getCoursesLessons } from 'store/courses-lessons/actions';
import {
  fetchCoursesForSchool,
  type CourseLessonRow,
  type LinkedCourse,
} from 'store/courses-lessons/helpers';
import { getSchools } from 'store/schools/actions';
import { schoolsSelector } from 'store/schools/selectors';
import { userSelector } from 'store/auth/selectors';

import { useSearch } from 'hooks/useSearch';
import { useModal } from 'hooks/useModal';

import { getTranslation } from 'helpers/getTranslation';

import { getLessonPathByCourseType } from 'helpers/getLessonPathByCourseType';

import { getCourseType } from 'components/CoursesTable/CoursesTable.helpers';

import { DEFAULT_COURSES_LESSONS_PER_PAGE } from './CoursesLessonsPage.pagination.config';
import { columns } from './CoursesLessonsPage.tableConfig';
import {
  DEFAULT_GROUP_BY,
  GROUP_BY_COURSE,
  GROUP_BY_OPTIONS,
  type GroupByOption,
  type GroupByValue,
} from './CoursesLessonsPage.grouping.config';
import {
  buildLessonGroups,
  shouldShowGroupHeaders,
  type DisplayRow,
} from './CoursesLessonsPage.grouping';

type SelectOption = {
  label: string;
  value: string;
};

export type CoursesLessonsSchoolOption = {
  label: string;
  value: string;
  name: string;
  preview?: string | null;
  language?: string | null;
};

export type CoursesLessonsViewMode = 'selection' | 'lessons';

export const useCoursesLessons = ({
  lessons,
  lessonsPerPage,
  currentPage,
}: {
  lessons: CourseLessonRow[];
  lessonsPerPage: number;
  currentPage: number;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const schools = useAppSelector(schoolsSelector);

  const isRootAdmin = user.role === USER_ROLES.ROOT_ADMIN;
  const userSchoolId = user.schoolId as string | undefined;

  const [
    removeConfirmOpen,
    openRemoveConfirm,
    closeRemoveConfirm,
    removeConfirmData,
  ] = useModal({
    defaultOpen: false,
  });

  const [attachDrawerOpen, setAttachDrawerOpen] = useState(false);
  const [selectedLessonForAttach, setSelectedLessonForAttach] =
    useState<CourseLessonRow | null>(null);

  const [replaceDrawerOpen, setReplaceDrawerOpen] = useState(false);
  const [selectedLessonForReplace, setSelectedLessonForReplace] =
    useState<CourseLessonRow | null>(null);

  const [viewMode, setViewMode] =
    useState<CoursesLessonsViewMode>('selection');
  const [schoolsLoading, setSchoolsLoading] = useState(isRootAdmin);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupByValue>(DEFAULT_GROUP_BY);
  const [sortParams, setSortParams] = useState({
    sortBy: 'createdAt',
    sortDirection: 'desc',
  });
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(
    () => (!isRootAdmin && userSchoolId ? userSchoolId : null),
  );
  const [selectedCourseOption, setSelectedCourseOption] =
    useState<SelectOption | null>(null);
  const [courseOptions, setCourseOptions] = useState<SelectOption[]>([]);

  const [searchQuery, onSearchChange] = useSearch();

  const schoolOptions = useMemo(
    () =>
      schools.map(
        (school: {
          id: string;
          name: string;
          preview?: string | null;
          language?: string | null;
        }) => ({
          label: school.name,
          value: school.id,
          name: school.name,
          preview: school.preview,
          language: school.language,
        }),
      ),
    [schools],
  );

  const selectedSchoolOption = useMemo(() => {
    if (!selectedSchoolId) {
      return null;
    }

    return (
      schoolOptions.find(
        (option: CoursesLessonsSchoolOption) =>
          option.value === selectedSchoolId,
      ) ?? {
        label: selectedSchoolId,
        value: selectedSchoolId,
        name: selectedSchoolId,
      }
    );
  }, [schoolOptions, selectedSchoolId]);

  const _getListParams = (overrides: Record<string, any> = {}) => {
    const params: Record<string, any> = {
      sortBy: sortParams.sortBy,
      sortDirection: sortParams.sortDirection,
      ...overrides,
    };

    if (searchQuery) {
      params.query = searchQuery;
    }

    if (selectedSchoolId) {
      params.schoolId = selectedSchoolId;
    }

    const courseId =
      overrides.courseIds ?? selectedCourseOption?.value ?? undefined;

    if (courseId) {
      params.courseIds = courseId;
    }

    if (!showArchived) {
      params.excludeStatus = STATUSES.ARCHIVED;
    }

    return params;
  };

  const listQueryParams = _getListParams();

  const refreshLessonsList = useCallback(async () => {
    if (viewMode !== 'lessons' || !selectedCourseOption?.value) {
      return;
    }

    try {
      await dispatch(
        getCoursesLessons({
          page: currentPage,
          limit: lessonsPerPage ?? DEFAULT_COURSES_LESSONS_PER_PAGE,
          ..._getListParams(),
        }),
      ).unwrap();
    } catch {
      // Thunk resolves with empty data when the API is unavailable.
    }
  }, [
    currentPage,
    dispatch,
    lessonsPerPage,
    searchQuery,
    showArchived,
    sortParams.sortBy,
    sortParams.sortDirection,
    selectedCourseOption?.value,
    selectedSchoolId,
    viewMode,
  ]);

  const lessonGroups = useMemo(
    () => buildLessonGroups(lessons, groupBy),
    [lessons, groupBy],
  );

  const tableColumns = useMemo(() => {
    if (groupBy === GROUP_BY_COURSE) {
      return columns.filter((column) => column.key !== 'courses');
    }

    return columns;
  }, [groupBy]);

  const showGroupHeaders = shouldShowGroupHeaders(groupBy);

  const currentGroupByOption = useMemo(
    () => GROUP_BY_OPTIONS.find((option) => option.value === groupBy),
    [groupBy],
  );

  useEffect(() => {
    if (!isRootAdmin) {
      return;
    }

    let cancelled = false;

    const loadSchools = async () => {
      setSchoolsLoading(true);

      try {
        await dispatch(getSchools()).unwrap();
      } catch {
        // Schools list stays empty when the API is unavailable.
      } finally {
        if (!cancelled) {
          setSchoolsLoading(false);
        }
      }
    };

    loadSchools();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isRootAdmin]);

  useEffect(() => {
    if (!selectedSchoolId) {
      setCourseOptions([]);
      return;
    }

    let cancelled = false;

    const loadCoursesForSelectedSchool = async () => {
      setCoursesLoading(true);

      try {
        const schoolCourses = await fetchCoursesForSchool(
          selectedSchoolId,
          store.getState,
        );

        if (cancelled) {
          return;
        }

        setCourseOptions(
          schoolCourses.map((course) => ({
            label: `${course.title} (${getCourseType(course.type)})`,
            value: course.id,
          })),
        );
      } catch {
        if (!cancelled) {
          setCourseOptions([]);
        }
      } finally {
        if (!cancelled) {
          setCoursesLoading(false);
        }
      }
    };

    loadCoursesForSelectedSchool();

    return () => {
      cancelled = true;
    };
  }, [selectedSchoolId]);

  useEffect(() => {
    if (!selectedCourseOption?.value || !selectedSchoolId) {
      return;
    }

    let cancelled = false;

    const loadLessonsForSelectedCourse = async () => {
      setLessonsLoading(true);

      try {
        await dispatch(
          getCoursesLessons({
            page: 1,
            limit: lessonsPerPage ?? DEFAULT_COURSES_LESSONS_PER_PAGE,
            ..._getListParams(),
          }),
        ).unwrap();
      } catch {
        // Thunk resolves with empty data when the API is unavailable.
      } finally {
        if (!cancelled) {
          setLessonsLoading(false);
        }
      }
    };

    loadLessonsForSelectedCourse();

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    lessonsPerPage,
    searchQuery,
    showArchived,
    sortParams.sortBy,
    sortParams.sortDirection,
    selectedCourseOption?.value,
    selectedSchoolId,
  ]);

  const handleSchoolChange = (
    nextSchoolOption: CoursesLessonsSchoolOption | null,
  ) => {
    setSelectedSchoolId(nextSchoolOption?.value ?? null);
    setSelectedCourseOption(null);
    setViewMode('selection');
  };

  const handleCourseChange = (nextCourseOption: SelectOption | null) => {
    setSelectedCourseOption(nextCourseOption);
    setViewMode(nextCourseOption ? 'lessons' : 'selection');
  };

  const handleChangeCourseClick = () => {
    setSelectedCourseOption(null);
    setViewMode('selection');
  };

  const handleShowArchivedChange = (event: any) => {
    setShowArchived(event.target.checked);
  };

  const handleGroupByChange = (nextGroupByOption: GroupByOption) => {
    setGroupBy(nextGroupByOption.value);
  };

  const handleLessonRowClick = (
    lesson: DisplayRow,
    groupCourse?: LinkedCourse,
  ) => {
    const targetCourse = groupCourse ?? lesson.groupCourse ?? lesson.courses[0];

    if (!targetCourse) {
      return;
    }

    navigate(
      getLessonPathByCourseType({
        courseType: targetCourse.type,
        courseId: targetCourse.id,
        lessonId: lesson.id,
      }),
    );
  };

  const handleLessonEditClick = (lesson: DisplayRow) => {
    handleLessonRowClick(lesson, lesson.groupCourse);
  };

  const handleLessonRemoveClick = (lesson: DisplayRow) => {
    openRemoveConfirm({
      title: getTranslation('courses_lessons_page.remove_confirm.title'),
      text: '',
      data: {
        item: lesson,
      },
    });
  };

  const handleConfirmRemoveClick = async ({ item }: { item: DisplayRow }) => {
    closeRemoveConfirm();

    try {
      await api.removeLessonById(item.id);
      await refreshLessonsList();
    } catch (error) {
      console.warn(error);
    }
  };

  const handleLessonAttachClick = (lesson: DisplayRow) => {
    setSelectedLessonForAttach(lesson);
    setAttachDrawerOpen(true);
  };

  const handleLessonReplaceClick = (lesson: DisplayRow) => {
    setSelectedLessonForReplace(lesson);
    setReplaceDrawerOpen(true);
  };

  const handleAttachDrawerClose = () => {
    setAttachDrawerOpen(false);
    setSelectedLessonForAttach(null);
  };

  const handleReplaceDrawerClose = () => {
    setReplaceDrawerOpen(false);
    setSelectedLessonForReplace(null);
  };

  const handleAttachmentsChange = async () => {
    await refreshLessonsList();
  };

  const handleSortChange = async (nextSortBy: string) => {
    if (viewMode !== 'lessons' || !selectedCourseOption?.value) {
      return;
    }

    const nextSortDirection =
      sortParams.sortDirection === 'asc' ? 'desc' : 'asc';

    try {
      await dispatch(
        getCoursesLessons({
          page: 1,
          limit: lessonsPerPage ?? DEFAULT_COURSES_LESSONS_PER_PAGE,
          ..._getListParams({
            sortBy: nextSortBy,
            sortDirection: nextSortDirection,
          }),
        }),
      ).unwrap();

      setSortParams({
        sortBy: nextSortBy,
        sortDirection: nextSortDirection,
      });
    } catch {
      // Thunk resolves with empty data when the API is unavailable.
    }
  };

  return {
    viewMode,
    isRootAdmin,
    schoolsLoading,
    coursesLoading,
    loading: lessonsLoading,
    groupBy,
    currentGroupByOption,
    searchQuery,
    sortParams,
    showArchived,
    lessonGroups,
    tableColumns,
    showGroupHeaders,
    listQueryParams,
    schoolOptions,
    selectedSchoolOption,
    courseOptions,
    selectedCourseOption,
    removeConfirmOpen,
    removeConfirmData,
    attachDrawerOpen,
    selectedLessonForAttach,
    replaceDrawerOpen,
    selectedLessonForReplace,
    closeRemoveConfirm,
    onSearchChange,
    onShowArchivedChange: handleShowArchivedChange,
    onGroupByChange: handleGroupByChange,
    onSchoolChange: handleSchoolChange,
    onCourseChange: handleCourseChange,
    onChangeCourseClick: handleChangeCourseClick,
    onSortChange: handleSortChange,
    onLessonRowClick: handleLessonRowClick,
    onLessonEditClick: handleLessonEditClick,
    onLessonRemoveClick: handleLessonRemoveClick,
    onLessonAttachClick: handleLessonAttachClick,
    onLessonReplaceClick: handleLessonReplaceClick,
    onConfirmRemoveClick: handleConfirmRemoveClick,
    onAttachDrawerClose: handleAttachDrawerClose,
    onReplaceDrawerClose: handleReplaceDrawerClose,
    onAttachmentsChange: handleAttachmentsChange,
  };
};
